import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { FaBell, FaWhatsapp } from 'react-icons/fa';
import { useParams } from 'react-router-dom'
import { httpsCallable } from "firebase/functions";

import { hooks } from '../hooks';
import { actions } from '../store/actions';
import { components } from '../components';
import { RootState } from '../store';
import { db, functions } from '../configs/firebaseConfig'

import bowl from '../assets/images/Bowl.gif';
import circle from '../assets/images/circle.png';

export const OrderSuccessful: React.FC = () => {

  const dispatch = useDispatch<ThunkDispatch<RootState, void, Action>>();
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>()

  const [timeLeft, setTimeLeft] = useState<any | null>(null);
  const [orderStatus, setOrderStatus] = useState<number>(0);
  const [orderData, setOrderData] = useState<any | null>(null);
  const [isBuzzerDisabled, setIsBuzzerDisabled] = useState(false)

  hooks.useThemeColor('#fff');

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(actions.setColor('#fff'));
  }, []);

  const handleNavigation = () => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate(`/${token}`);
    }
  };

  const handleWhatsAppShare = () => {
    if (!orderId) return

    const url = `${window.location.origin}/view-order/${orderId}`
    const message = `If you wish to open this order, click here:\n${url}\n\nPlease get this order on your WhatsApp. This will help you to open this interface in case you close this web interface by mistake.`
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`
    window.open(whatsappUrl, '_blank')
  }

  const calculateInitialTime = (): number => {
    const storedTime = localStorage.getItem('estimated_time');
    if (storedTime) {
      const parsed = parseInt(storedTime, 10);
      return !isNaN(parsed) ? parsed * 60 : 0;
    }
    return 0;
  };

  useEffect(() => {

    if (!orderData?.createdAt) return;

    const initialTime = calculateInitialTime();
    const extensionTime = 5 * 60;

    const createdAtMs = orderData.createdAt.seconds * 1000 + orderData.createdAt.nanoseconds / 1_000_000;
    const now = Date.now();
    const elapsed = Math.floor((now - createdAtMs) / 1000);

    let totalExtendedSeconds = initialTime;

    const storedExtensions = Number(localStorage.getItem('extensionCount')) || 0;
    totalExtendedSeconds += storedExtensions * extensionTime;

    let remaining = totalExtendedSeconds - elapsed;

    if (remaining <= 0 && orderStatus !== 2) {
      // Need to add another extension now
      const newCount = storedExtensions + 1;
      localStorage.setItem('extensionCount', newCount.toString());
      remaining = extensionTime;
    }

    setTimeLeft(Math.max(remaining, 0));

    const interval = setInterval(() => {
      setTimeLeft((prev: any) => {
        if (prev === 0 && orderStatus !== 2) {
          // Add another 5-minute extension
          const count = Number(localStorage.getItem('extensionCount')) || 0;
          const newCount = count + 1;
          localStorage.setItem('extensionCount', newCount.toString());
          return extensionTime;
        }

        return prev > 0 ? prev - 1 : 0;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [orderData?.createdAt, orderStatus]);

  useEffect(() => {
    if (orderId) {

      const orderRef = doc(db, 'orders', orderId);

      const unsubscribe = onSnapshot(orderRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const currentStatus = data.orderStatus;
          setOrderStatus(currentStatus);
          setOrderData(data)

          if (currentStatus === 2) {
            localStorage.removeItem('estimated_time');
            setTimeLeft(0);
            handleNavigation();
          }
        }
      });

      return () => unsubscribe();
    }
  }, [orderId]);

  const handleBuzzerClick = async () => {
    if (isBuzzerDisabled || !orderData?.waiter?.id) return

    const callToWaiter = httpsCallable(functions, "callToWaiter")

    try {
      setIsBuzzerDisabled(true)

      const result = await callToWaiter({
        waiterId: orderData.waiter.id
      });

      // console.log("Notification result:", result.data)
    } catch (error) {
      console.log(error)
      if (error instanceof Error) {
        console.error("Failed to send notification:", error.message)
      } else {
        console.error("Failed to send notification:", error)
      }
    } finally {
      setIsBuzzerDisabled(false)
    }
  }


  const formatTime = (time: number | null): string => {
    if (time === null) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes < 10 ? '0' : ''}${minutes} : ${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const smallScreen = window.innerWidth === 320 && window.innerHeight === 568 ||
    window.innerWidth === 360 && window.innerHeight === 640 ||
    window.innerWidth === 375 && window.innerHeight === 667;

  const renderHeader = (): JSX.Element => (
    <components.Header showBurger={true} showButton={true} />
  );

  const renderContent = (): JSX.Element => (
    <main className='scrollable center' style={{ zIndex: 1, backgroundColor: '#f2f4f4', color: '#000' }}>
      <div className='container'>
        <div style={{ marginBottom: 20, marginTop: timeLeft ? (smallScreen ? '100%' : '20%') : '20%' }}>
          <img
            src={bowl}
            alt="Delicious Dish"
            style={{
              width: '200px',
              maxWidth: '400px',
              height: '200px',
              margin: '0 auto 20px',
              display: 'block',
            }}
          />
        </div>

        {orderStatus === 0 && (
          <>
            <p className='t18' style={{ textAlign: 'center', fontSize: 16, fontWeight: 300 }}>
              Thank you! We've received your order.
            </p>
            <p className='t18' style={{ textAlign: 'center', fontSize: 16, fontWeight: 300 }}>
              Our team is reviewing it and will begin preparing it soon.
            </p>
          </>
        )}

        {orderStatus === 1 && (
          <>
            <p className='t18' style={{ textAlign: 'center', fontSize: 16, fontWeight: 300 }}>
              Your delicious meal is now being prepared
            </p>
            <p className='t18' style={{ textAlign: 'center', fontSize: 16, fontWeight: 300 }}>
              with great care. It’ll be ready shortly!
            </p>
          </>
        )}

        {orderStatus === 2 && (
          <>
            <p className='t18' style={{ textAlign: 'center', fontSize: 16, fontWeight: 300 }}>
              Enjoy your meal!
            </p>
            <p className='t18' style={{ textAlign: 'center', fontSize: 16, fontWeight: 300 }}>
              Your order has been delivered. Bon appétit!
            </p>
          </>
        )}

        <p className='t18' style={{
          textAlign: 'center',
          marginTop: 10,
          marginBottom: 40,
          fontSize: smallScreen ? 16 : 18,
          fontWeight: 800
        }}>
          Get ready to savor the delight!
        </p>

        {timeLeft !== null && (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <p className='t18' style={{ textAlign: 'center', marginBottom: 0, fontWeight: 500, fontSize: 16 }}>
              Wait Time
            </p>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="t18" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 800, fontSize: 48 }}>{formatTime(timeLeft)}</span>
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  <span style={{ fontSize: 14, fontWeight: 300, marginRight: 15 }}>Min</span>
                  <span style={{ fontSize: 14, fontWeight: 300, marginLeft: 15 }}>Sec</span>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <img
                src={circle}
                alt="Timer Icon"
                style={{
                  width: '40px',
                  height: '40px',
                  margin: '12px auto 10px',
                  display: 'block',
                }}
              />
            </div>
          </div>
        )}

        <div style={{ marginBottom: 30 }}>
          <components.Button
            text='Add More Dishes'
            variant="inverse"
            onClick={handleNavigation}
          />
        </div>

        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <button
            onClick={handleWhatsAppShare}
            style={{
              backgroundColor: '#25D366',
              fontFamily: 'Poppins',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '25px',
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: 16
            }}
          >
            <FaWhatsapp style={{ marginRight: 8 }} />
            Share on WhatsApp
          </button>
        </div>

        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <button
            onClick={handleBuzzerClick}
            disabled={isBuzzerDisabled}
            style={{
              background: '#fdd835',
              fontFamily: 'Poppins',
              border: 'none',
              borderRadius: 20,
              padding: '5px 20px',
              fontSize: 16,
              cursor: isBuzzerDisabled ? 'not-allowed' : 'pointer',
              opacity: isBuzzerDisabled ? 0.5 : 1,
              display: 'inline-flex',
              alignItems: 'center'
            }}
          >
            <FaBell style={{ marginRight: 8 }} /> Call waiter
          </button>
        </div>

      </div>
    </main>
  );

  return (
    <>
      {renderHeader()}
      {renderContent()}
    </>
  );
};

